import {
    Avatar,
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    Menu,
    MenuItem,
    TextField,
    Typography,
    alpha,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useEvent } from '../../hooks/useEvent';
import socket from "../../hooks/useSocket";
import api from '../../services/api';
import ToastError from '../../toast/error/toastError';
import ToastSuccess from '../../toast/success/toastSuccess';
import DropZoneIconUpload from '../DropZoneIconUpload';
import EmojiPicker from '../EmojiPicker';

// Icons with minimal imports
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import VideocamIcon from '@mui/icons-material/Videocam';
import { AuthContext } from '../../context/Auth/AuthContext';

function removeDuplicateComments(comments) {
    const seenIds = new Set();
    return comments.filter(comment => {
        if (seenIds.has(comment.id)) {
            return false; // Já vimos esse id, então remove
        }
        seenIds.add(comment.id);
        return true; // Primeira vez que vemos esse id, então mantém
    });
}

// Improved styled components with modern design elements
const ChatContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRadius: 12,
    overflow: 'hidden',
    height: '100%',
    maxHeight: '700px',
    position: 'relative',
    boxShadow: theme.palette.mode === 'light'
        ? '0 2px 10px rgba(0,0,0,0.05)'
        : '0 2px 10px rgba(0,0,0,0.2)',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
        boxShadow: theme.palette.mode === 'light'
            ? '0 4px 15px rgba(0,0,0,0.08)'
            : '0 4px 15px rgba(0,0,0,0.25)',
    }
}));

const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    height: 56,
    backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.85)
        : alpha(theme.palette.background.default, 0.7),
    backdropFilter: 'blur(12px)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    transition: 'background-color 0.2s ease',
}));

const MessagesList = styled(List)(({ theme }) => ({
    flexGrow: 1,
    overflowY: 'auto',
    padding: '12px 18px 12px 18px',
    backgroundImage: theme.palette.mode === 'light'
        ? 'linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23f0f0f0\' fill-opacity=\'0.2\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'
        : 'none',
    '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: alpha(theme.palette.mode === 'dark' ? '#ffffff' : '#000000', 0.15),
        borderRadius: 6,
        '&:hover': {
            backgroundColor: alpha(theme.palette.mode === 'dark' ? '#ffffff' : '#000000', 0.25),
        }
    },
    scrollBehavior: 'smooth',
}));

const MessageBubble = styled(Box)(({ theme, isMine }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 14px',
    borderRadius: isMine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
    width: '100%',
    backgroundColor: isMine
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.09)
        : theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.6) : alpha(theme.palette.background.default, 0.8),
    border: `1px solid ${isMine
        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.15)
        : alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.06)}`,
    marginBottom: 2,
    transition: 'all 0.2s ease',
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.03)}`,
    '&:hover': {
        backgroundColor: isMine
            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.13)
            : theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.75) : alpha(theme.palette.background.default, 0.95),
        boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
    }
}));

const MessageContainer = styled(Box)(({ theme, isReply }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 14,
    paddingLeft: isReply ? 28 : 0,
    width: '100%',
    position: 'relative',
    '&.highlight-comment': {
        animation: 'highlight-pulse 2.5s ease-out',
    },
    '&::before': isReply ? {
        content: '""',
        position: 'absolute',
        left: 12,
        top: 0,
        bottom: 0,
        width: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
        borderRadius: 4,
    } : {},
    '@keyframes highlight-pulse': {
        '0%': {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            borderRadius: '8px'
        },
        '100%': {
            backgroundColor: 'transparent',
        }
    }
}));

const ChatInputContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    backgroundColor: theme.palette.background.paper,
    padding: '16px 18px',
    borderTop: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    bottom: 0,
    left: 0,
    width: '100%',
    zIndex: 10,
    boxShadow: `0 -2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
}));

const ChatInputRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.6)
        : alpha(theme.palette.background.default, 0.7),
    borderRadius: 24,
    padding: '6px 10px',
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    transition: 'all 0.2s ease',
    '&:focus-within': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.7)
            : alpha(theme.palette.background.default, 0.9),
    },
}));

const MediaPreviewContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    padding: '4px 10px',
    borderRadius: 8,
    backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.4)
        : alpha(theme.palette.background.default, 0.5),
}));

const MediaItem = styled(Box)(({ theme }) => ({
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: theme.shadows[2],
    width: 72,
    height: 72,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        '& .media-actions': {
            opacity: 1,
        },
    },
}));

const MediaActions = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 4,
    backgroundColor: alpha('#000000', 0.7),
    borderRadius: '0 0 0 8px',
    opacity: 0,
    transition: 'opacity 0.2s ease',
}));

const NewMessageIndicator = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 86,
    right: 24,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: 24,
    padding: '6px 14px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: `0 3px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
    cursor: 'pointer',
    zIndex: 5,
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-3px) scale(1.03)',
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.45)}`,
    },
}));

// Main component
const KanbanComments = ({ cardId }) => {
    const theme = useTheme();
    const emitter = useEvent();
    const { user } = useContext(AuthContext);

    // States
    const [commentsArray, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [replyingToComment, setReplyingToComment] = useState(null);
    const [editingComment, setEditingComment] = useState(null);
    const [editText, setEditText] = useState('');
    const [files, setFiles] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [currentComment, setCurrentComment] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [filter, setFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('newest');
    const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
    const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
    const [openMediaDialog, setOpenMediaDialog] = useState(false);
    const [currentMediaUrl, setCurrentMediaUrl] = useState('');
    const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [highlightedComment, setHighlightedComment] = useState(null);

    const comments = removeDuplicateComments(commentsArray);

    // Refs
    const listRef = useRef(null);
    const textFieldRef = useRef(null);
    const dropzoneRef = useRef(null);
    const uploadAbortController = useRef(null);
    const commentsInitialized = useRef(false);
    const scrollTimeout = useRef(null);
    const observerRef = useRef(null);
    const lastCommentRef = useRef(null);
    const commentRefs = useRef({});

    // Filter comments
    const filteredComments = useMemo(() => {
        if (!comments || comments.length === 0) return [];
        let result = [...comments];

        if (filter === 'media') {
            result = result.filter(comment => comment.medias && comment.medias.length > 0);
        } else if (filter === 'text') {
            result = result.filter(comment => (!comment.medias || comment.medias.length === 0));
        }

        return result;
    }, [comments, filter]);

    // Scroll to comment function
    const scrollToComment = useCallback((commentId) => {
        setTimeout(() => {
            const element = commentRefs.current[commentId];
            if (element) {
                // Scroll the comment into view
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Highlight the comment temporarily
                setHighlightedComment(commentId);
                setTimeout(() => {
                    setHighlightedComment(null);
                }, 2000);
            }
        }, 100);
    }, []);

    // Setup scroll observer to detect when user scrolls away from bottom
    useEffect(() => {
        if (!listRef.current) return;

        // Function to check if user has scrolled away from bottom
        const checkIfScrolledAway = () => {
            if (!listRef.current) return;

            const { scrollTop, scrollHeight, clientHeight } = listRef.current;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

            // Only update indicator if we're scrolled away and there are unread messages
            if (!isAtBottom && unreadCount > 0) {
                setShowNewMessageIndicator(true);
            } else {
                setShowNewMessageIndicator(false);
                setUnreadCount(0);
            }
        };

        // Add scroll listener
        const listElement = listRef.current;
        listElement.addEventListener('scroll', checkIfScrolledAway);

        return () => {
            if (listElement) {
                listElement.removeEventListener('scroll', checkIfScrolledAway);
            }
        };
    }, [unreadCount]);

    // Load comments
    const fetchComments = useCallback(async (reset = false) => {
        if (isLoading || (!hasMore && !reset) || !cardId) return;

        try {
            setIsLoading(true);
            const currentPage = reset ? 1 : page;

            const { data } = await api.get(`/kanban/comment/${cardId}`, {
                params: {
                    page: currentPage,
                    limit: 15,
                    sort: sortOrder === 'newest' ? 'desc' : 'asc'
                }
            });

            const commentsData = Array.isArray(data) ? data : (data.results || []);

            // Apply proper sequential listing
            if (reset) {
                setComments(commentsData);
            } else {
                if (sortOrder === 'newest') {
                    setComments(prev => [...prev, ...commentsData]);
                } else {
                    setComments(prev => [...commentsData, ...prev]);
                }
            }

            setHasMore(commentsData.length === 15);
            setPage(reset ? 2 : currentPage + 1);

            // Scroll to bottom after initial load if this is newest sort order
            if (reset && sortOrder === 'newest' && commentsData.length > 0) {
                scrollToLatest(true);
            }
        } catch (error) {
            console.error('Error loading comments:', error);
            ToastError('Could not load comments');
        } finally {
            setIsLoading(false);
        }
    }, [cardId, hasMore, isLoading, page, sortOrder]);

    const fetchCommentsRef = useRef(fetchComments);

    useEffect(() => {
        fetchCommentsRef.current = fetchComments;
    }, [fetchComments]);

    // Initialize comments
    useEffect(() => {
        if (cardId && !commentsInitialized.current) {
            commentsInitialized.current = true;
            fetchCommentsRef.current(true);
        }
        return () => {
            if (cardId !== null) commentsInitialized.current = false;
        };
    }, [cardId]);

    // Scroll to latest message function (improved)
    const scrollToLatest = useCallback((immediate = false) => {
        if (listRef.current) {
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }

            // Use requestAnimationFrame for smoother scrolling
            scrollTimeout.current = setTimeout(() => {
                requestAnimationFrame(() => {
                    // For newest sort order, scroll to top
                    // For oldest sort order, scroll to bottom
                    if (sortOrder !== 'newest') {
                        listRef.current.scrollTop = 0;
                    } else {
                        listRef.current.scrollTop = listRef?.current?.scrollHeight || 0;
                    }

                    // Clear the unread indicator
                    setShowNewMessageIndicator(false);
                    setUnreadCount(0);
                });
            }, immediate ? 0 : 100);
        }
    }, [sortOrder]);

    // Focus text field when replying
    useEffect(() => {
        if (textFieldRef.current && replyingToComment) {
            setTimeout(() => textFieldRef.current.focus(), 100);
        }
    }, [replyingToComment]);

    // Socket setup with improved message handling
    useEffect(() => {
        if (!cardId) return;

        const onNewComment = (data) => {
            if (data.cardId === cardId) {
                if (data.action === 'create') {
                    // Check if the message is from current user
                    const isFromCurrentUser = data.comment.user?.id === user.id;

                    setComments(prev => {
                        const exists = prev.some(comment => comment.id === data.comment.id);
                        if (exists) return prev;

                        // Add new comment in the right position based on sort order
                        if (sortOrder === 'newest') {
                            return [data.comment, ...prev];
                        } else {
                            return [...prev, data.comment];
                        }
                    });

                    // Scroll to latest only if from current user, otherwise show indicator
                    if (isFromCurrentUser) {
                        scrollToLatest(true);
                    } else {
                        // Check if user has scrolled away from latest messages
                        if (listRef.current) {
                            const { scrollTop, scrollHeight, clientHeight } = listRef.current;
                            const isScrolledAway = scrollHeight - scrollTop - clientHeight > 50;

                            if (isScrolledAway) {
                                setUnreadCount(prev => prev + 1);
                                setShowNewMessageIndicator(true);
                            } else {
                                // User is at bottom, so scroll to show new message
                                scrollToLatest(true);
                            }
                        }
                    }
                } else if (data.action === 'update') {
                    setComments(prev =>
                        prev.map(comment =>
                            comment.id === data.comment.id ? data.comment : comment
                        )
                    );
                } else if (data.action === 'delete') {
                    setComments(prev =>
                        prev.filter(comment => comment.id !== data.commentId)
                    );
                }
            }
        };

        socket.off('kanban-comments');
        socket.on('kanban-comments', onNewComment);

        return () => {
            socket.off('kanban-comments', onNewComment);
        };
    }, [cardId, user.id, scrollToLatest, sortOrder]);

    // Send comment with media support
    const handleSendMessage = async () => {
        if ((commentText.trim() === '' && files.length === 0) || isLoading || !cardId) return;

        try {
            setIsLoading(true);
            uploadAbortController.current = new AbortController();

            const formData = new FormData();

            if (commentText.trim() !== '') {
                formData.append('text', commentText.trim());
            }

            if (replyingToComment) {
                formData.append('parentId', replyingToComment.id);
            }

            // Add files to FormData
            files.forEach((file) => {
                formData.append('medias', file);
            });

            // Send with progress tracking
            const { data } = await api.post(`/kanban/comment/${cardId}`, formData, {
                signal: uploadAbortController.current.signal,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Emit event for real-time updates
            emitter.emit('kanban-comments', {
                action: 'create',
                comment: data,
                cardId: cardId,
            });

            // Add to list and clear state
            if (sortOrder === 'newest') {
                setComments(prev => [data, ...prev]);
            } else {
                setComments(prev => [...prev, data]);
            }

            ToastSuccess('Comment sent');

            // Reset states
            setCommentText('');
            setFiles([]);
            setReplyingToComment(null);

            // Scroll to see the new comment
            scrollToLatest(true);

        } catch (error) {
            console.error('Error sending comment:', error);
            ToastError('Could not send comment');
        } finally {
            setIsLoading(false);
            uploadAbortController.current = null;
        }
    };

    // File handling
    const handleDrop = useCallback(
        (acceptedFiles) => {
            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                })
            );
            setFiles([...files, ...newFiles]);
        },
        [files]
    );

    // Remove file
    const handleRemoveFile = (fileToRemove) => {
        setFiles(prev => prev.filter(file => file !== fileToRemove));
        URL.revokeObjectURL(fileToRemove.preview);
    };

    // Paste handling for images
    const handlePaste = (event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItems = items.filter(item => item.kind === 'file' && item.type.startsWith('image/'));

        if (imageItems.length > 0) {
            event.preventDefault();
            const files = imageItems.map(item => item.getAsFile()).filter(Boolean);
            handleDrop(files);
        }
    };

    // Open upload dialog
    const handleUploadTrigger = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.triggerUpload();
        }
    };

    // Comment menu handling
    const handleOpenMenu = (event, comment) => {
        setMenuAnchorEl(event.currentTarget);
        setCurrentComment(comment);
    };

    const handleCloseMenu = (clearCurrent = true) => {
        setMenuAnchorEl(null);
        if (clearCurrent) {
            setCurrentComment(null);
        }
    };

    // Edit comment
    const handleStartEditing = () => {
        setEditingComment(currentComment);
        setEditText(currentComment.text || '');
        handleCloseMenu();
    };

    const handleSaveEdit = async () => {
        if (!editingComment || editText.trim() === editingComment.text) {
            setEditingComment(null);
            return;
        }

        try {
            setIsLoading(true);
            const { data } = await api.put(`/kanban/comment/${editingComment.id}`, {
                text: editText.trim()
            });

            setComments(prev =>
                prev.map(comment =>
                    comment.id === editingComment.id ? { ...comment, text: editText.trim() } : comment
                )
            );

            emitter.emit('kanban-comments', {
                action: 'update',
                comment: data,
                cardId: cardId,
            });

            ToastSuccess('Comment updated');
        } catch (error) {
            console.error('Error editing comment:', error);
            ToastError('Could not edit comment');
        } finally {
            setIsLoading(false);
            setEditingComment(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingComment(null);
    };

    // Delete comment
    const handleDeleteComment = () => {
        setOpenDeleteDialog(true);
        handleCloseMenu(false);
    };

    const handleConfirmDelete = async () => {
        if (!currentComment) return;

        try {
            setIsLoading(true);
            await api.delete(`/kanban/comment/${currentComment.id}`);

            setComments(prev => prev.filter(comment => comment.id !== currentComment.id));

            emitter.emit('kanban-comments', {
                action: 'delete',
                commentId: currentComment.id,
                cardId: cardId,
            });

            ToastSuccess('Comment deleted');
        } catch (error) {
            console.error('Error deleting comment:', error);
            ToastError('Could not delete comment');
        } finally {
            setIsLoading(false);
            setOpenDeleteDialog(false);
            setCurrentComment(null);
        }
    };

    // Like comment
    const handleToggleLike = async (comment) => {
        try {
            const isLiked = comment.likes?.includes(user.id);

            const { data } = await api.post(`/kanban/comment/${comment.id}/reaction`, {
                type: 'like',
                action: isLiked ? 'remove' : 'add'
            });

            setComments(prev =>
                prev.map(c =>
                    c.id === comment.id ? { ...c, likes: data.likes } : c
                )
            );
        } catch (error) {
            console.error('Error reacting to comment:', error);
            ToastError('Could not register your reaction');
        }
    };

    // Media view
    const handleOpenMedia = (mediaUrl) => {
        setCurrentMediaUrl(mediaUrl);
        setOpenMediaDialog(true);
    };

    // Format date
    const formatRelativeDate = (date) => {
        return formatDistance(new Date(date), new Date(), {
            addSuffix: true,
            locale: ptBR
        });
    };

    // Render media icon based on file extension
    const renderMediaIcon = (mediaUrl) => {
        const lowerUrl = mediaUrl.toLowerCase();

        // Image extensions
        if (lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') ||
            lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif') ||
            lowerUrl.endsWith('.webp') || lowerUrl.endsWith('.svg')) {
            return <ImageIcon fontSize="small" />;
        }
        // Video extensions
        else if (lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') ||
            lowerUrl.endsWith('.avi') || lowerUrl.endsWith('.mov') ||
            lowerUrl.endsWith('.wmv')) {
            return <VideocamIcon fontSize="small" />;
        }
        // Audio extensions
        else if (lowerUrl.endsWith('.mp3') || lowerUrl.endsWith('.wav') ||
            lowerUrl.endsWith('.ogg') || lowerUrl.endsWith('.m4a') ||
            lowerUrl.endsWith('.aac')) {
            return <AudioFileIcon fontSize="small" />;
        }
        // PDF
        else if (lowerUrl.endsWith('.pdf')) {
            return <PictureAsPdfIcon fontSize="small" />;
        }
        // Default for other file types
        else {
            return <InsertDriveFileIcon fontSize="small" />;
        }
    };

    const renderCommentMedia = (media) => {
        const { mediaUrl } = media;
        const lowerUrl = mediaUrl.toLowerCase();

        const isImage = lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') ||
            lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif') ||
            lowerUrl.endsWith('.webp') || lowerUrl.endsWith('.svg');

        const isVideo = lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') ||
            lowerUrl.endsWith('.avi') || lowerUrl.endsWith('.mov') ||
            lowerUrl.endsWith('.wmv');

        const isAudio = lowerUrl.endsWith('.mp3') || lowerUrl.endsWith('.wav') ||
            lowerUrl.endsWith('.ogg') || lowerUrl.endsWith('.m4a') ||
            lowerUrl.endsWith('.aac');

        const isPdf = lowerUrl.endsWith('.pdf');

        return (
            <Box
                key={media.id || mediaUrl}
                sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    mb: 1,
                    transition: 'transform 0.2s',
                    '&:hover': {
                        transform: isImage ? 'scale(1.02)' : 'none',
                    }
                }}
            >
                {isImage && (
                    <Box
                        component="img"
                        src={mediaUrl}
                        alt="Media"
                        onClick={() => handleOpenMedia(mediaUrl)}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '180px',
                            objectFit: 'contain',
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': {
                                opacity: 0.9,
                            }
                        }}
                    />
                )}

                {isVideo && (
                    <Box component="video" controls sx={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 1 }}>
                        <source src={mediaUrl} />
                        Your browser does not support this video.
                    </Box>
                )}

                {isAudio && (
                    <Box component="audio" controls sx={{ width: '100%', height: 40, borderRadius: 1 }}>
                        <source src={mediaUrl} />
                        Your browser does not support this audio.
                    </Box>
                )}

                {!isImage && !isVideo && !isAudio && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            cursor: 'pointer',
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#000000', 0.05),
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.08),
                            }
                        }}
                        onClick={() => window.open(mediaUrl, '_blank')}
                    >
                        {mediaUrl && renderMediaIcon(mediaUrl)}
                        <Typography variant="caption" sx={{ ml: 1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                            {mediaUrl.split('/').pop()}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // Render individual comment - WhatsApp-style
    const renderComment = (comment, index, commentsList) => {
        const isEditing = editingComment?.id === comment.id;
        const hasMedia = comment.medias && comment.medias.length > 0;
        const isLiked = comment.likes?.includes(user.id);
        const likesCount = comment.likes?.length || 0;

        // Verificando parentId e também parentComment
        const isReply = comment.parentId !== null;
        const parent = comment.parent || comment.parentComment;

        const isMine = comment.user?.id === user.id;
        const isLast = index === commentsList.length - 1;
        const commentRef = isLast ? lastCommentRef : null;
        const isHighlighted = highlightedComment === comment.id;

        return (
            <MessageContainer
                key={comment.id}
                id={`comment-${comment.id}`}
                isReply={isReply}
                className={`message-container ${isHighlighted ? 'highlight-comment' : ''}`}
                ref={el => {
                    // Salvando refs para todos os comentários
                    commentRefs.current[comment.id] = el;
                    // Se for o último, também salva na lastCommentRef
                    if (isLast && commentRef) {
                        commentRef.current = el;
                    }
                }}
            >
                <Box sx={{ display: 'flex', width: '100%' }}>
                    <Avatar
                        src={comment.user?.media}
                        alt={comment.user?.name}
                        sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.9rem',
                            mr: 1.5,
                            mt: 0.5
                        }}
                    >
                        {comment.user?.name?.charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        maxWidth: 'calc(100% - 40px)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    color: isMine ? theme.palette.primary.main : 'inherit',
                                    mr: 1
                                }}
                            >
                                {comment.user?.name} {user.id === comment.user?.id && ' (Você)'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.7rem',
                                    color: theme.palette.text.secondary,
                                    flexShrink: 0
                                }}
                            >
                                {formatRelativeDate(comment.createdAt)}
                                {comment.updatedAt !== comment.createdAt && ' • editado'}
                            </Typography>
                        </Box>

                        <MessageBubble isMine={isMine}>
                            {/* WhatsApp style quote for reply */}
                            {isReply && parent && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                                        pl: 1.5,
                                        pr: 1,
                                        py: 0.75,
                                        mb: 1,
                                        borderRadius: '0 4px 4px 0',
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            theme.palette.mode === 'dark' ? 0.08 : 0.05
                                        ),
                                        maxHeight: '80px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: alpha(
                                                theme.palette.primary.main,
                                                theme.palette.mode === 'dark' ? 0.12 : 0.08
                                            ),
                                        }
                                    }}
                                    onClick={() => scrollToComment(parent.id)}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            display: 'block',
                                            color: theme.palette.primary.main,
                                            fontSize: '0.7rem',
                                            mb: 0.25
                                        }}
                                    >
                                        {parent.user?.name} {user.id === parent.user?.id && ' (Você)'}
                                    </Typography>

                                    {/* Texto do comentário pai com truncamento */}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: theme.palette.text.primary,
                                            fontSize: '0.75rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            display: '-webkit-box'
                                        }}
                                    >
                                        {parent.text || ''}
                                    </Typography>

                                    {/* Indicador de mídia se o pai tiver mídia */}
                                    {parent.medias && parent.medias.length > 0 && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mt: 0.5,
                                            color: theme.palette.text.secondary
                                        }}>
                                            <ImageIcon sx={{ fontSize: '0.85rem', mr: 0.5 }} />
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                Mídia
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Gradiente para indicar truncamento em mensagens longas */}
                                    {(parent.text && parent.text.length > 100) && (
                                        <Box sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            height: '24px',
                                            background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0)}, ${alpha(theme.palette.background.paper, 0.6)})`,
                                        }} />
                                    )}
                                </Box>
                            )}

                            {isEditing ? (
                                <Box sx={{ width: '100%', minWidth: 200 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        variant="outlined"
                                        size="small"
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        autoFocus
                                        sx={{ mb: 1 }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={handleCancelEdit}
                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleSaveEdit}
                                            disabled={editText.trim() === '' || editText.trim() === comment.text}
                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                        >
                                            Salvar
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    {comment.text && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.85rem',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {comment.text}
                                        </Typography>
                                    )}

                                    {hasMedia && (
                                        <Box sx={{ mt: 1, width: '100%' }}>
                                            {comment.medias.map(media => renderCommentMedia(media))}
                                        </Box>
                                    )}
                                </>
                            )}
                        </MessageBubble>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                opacity: 0.7,
                                '&:hover': { opacity: 1 }
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setReplyingToComment(comment)}
                                sx={{ p: 0.5 }}
                            >
                                <ReplyIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                            {user.id === comment.user?.id &&
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenMenu(e, comment)}
                                    sx={{ p: 0.5 }}
                                >
                                    <MoreHorizIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                            }
                        </Box>
                    </Box>
                </Box>

                {/* Render replies */}
                {comment.replies && comment.replies.length > 0 && (
                    <Box sx={{ mt: 1, ml: 4, pl: 1, borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                        {comment.replies.map((reply, idx) => renderComment(reply, idx, comment.replies))}
                    </Box>
                )}
            </MessageContainer>
        );
    };

    // Get sorted comments in proper order for rendering
    const getDisplayComments = () => {
        if (sortOrder === 'newest') {
            return [...filteredComments].reverse();
        }
        return filteredComments;
    };

    return (
        <>
            <ChatContainer>
                <ChatHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                            Conversas
                        </Typography>
                        <Chip
                            label={comments.length}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main
                            }}
                        />
                    </Box>

                    {/*
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={(e) => setFilterMenuAnchor(e.currentTarget)}>
                            <TuneIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>

                        <IconButton size="small" onClick={(e) => setSortMenuAnchor(e.currentTarget)}>
                            <SortIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                    </Box>
                   */}
                </ChatHeader>

                <MessagesList ref={listRef}>
                    {isLoading && comments.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : filteredComments.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                            <Typography variant="body2" color="text.secondary" align="center">
                                {filter !== 'all'
                                    ? 'Nenhum comentário com este filtro'
                                    : 'Sem mensagens'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" align="center" sx={{ opacity: 0.7, mt: 1 }}>
                                {filter !== 'all'
                                    ? 'Tente outro filtro ou adicione um novo'
                                    : 'Inicie uma conversa'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {hasMore && (
                                <Button
                                    onClick={() => fetchCommentsRef.current()}
                                    disabled={isLoading}
                                    sx={{
                                        margin: '8px auto',
                                        display: 'flex',
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        color: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        borderRadius: 12,
                                    }}
                                    variant="outlined"
                                    size="small"
                                >
                                    {isLoading ? (
                                        <>
                                            <CircularProgress size={14} sx={{ mr: 1 }} />
                                            Carregando...
                                        </>
                                    ) : 'Carregar mais'}
                                </Button>
                            )}

                            {/* Render messages in correct order - Trello style */}
                            {getDisplayComments().map((comment, index, list) =>
                                renderComment(comment, index, list)
                            )}
                        </>
                    )}
                </MessagesList>

                {/* New message indicator button */}
                {showNewMessageIndicator && (
                    <NewMessageIndicator onClick={scrollToLatest}>
                        <Typography variant="caption" sx={{ mr: 1, fontWeight: 500 }}>
                            {unreadCount} {unreadCount === 1 ? 'nova mensagem' : 'novas mensagens'}
                        </Typography>
                        <KeyboardArrowDownIcon fontSize="small" />
                    </NewMessageIndicator>
                )}

                {/* Reply indicator */}
                {replyingToComment && (
                    <Box sx={{
                        p: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderLeft: `2px solid ${theme.palette.primary.main}`,
                            pl: 1,
                            py: 0.5
                        }}>
                            <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                                Respondendo a {replyingToComment.user.name}
                            </Typography>
                            <IconButton size="small" onClick={() => setReplyingToComment(null)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                )}

                {/* Input area - Trello style */}
                <ChatInputContainer>
                    <DropZoneIconUpload
                        multiple
                        files={files}
                        ref={dropzoneRef}
                        onTriggerUpload={dropzoneRef.current?.triggerUpload}
                        onDrop={handleDrop}
                        onRemove={handleRemoveFile}
                    />

                    <ChatInputRow>
                        <EmojiPicker
                            id="emojiPicker"
                            disabled={isLoading}
                            value={''}
                            pickerSx={{ marginLeft: '30px' }}
                            setValue={(emoji) => {
                                setCommentText(prev => prev + emoji);
                                if (textFieldRef.current) {
                                    textFieldRef.current.focus();
                                }
                            }}
                        />

                        <IconButton
                            onClick={handleUploadTrigger}
                            disabled={files.length >= 5 || isLoading}
                            size="small"
                        >
                            <Badge badgeContent={files.length} color="primary" invisible={files.length === 0}>
                                <AttachFileIcon fontSize="small" />
                            </Badge>
                        </IconButton>

                        <TextField
                            size="small"
                            variant="standard"
                            disabled={isLoading}
                            multiline
                            fullWidth
                            placeholder={replyingToComment
                                ? `Responder a ${replyingToComment.user.name}...`
                                : "Digite sua mensagem..."}
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onPaste={handlePaste}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            maxRows={4}
                            inputRef={textFieldRef}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: '0.85rem',
                                    padding: '6px 0',
                                }
                            }}
                        />

                        <IconButton
                            disabled={(commentText.trim() === '' && files.length === 0) || isLoading}
                            onClick={handleSendMessage}
                            size="small"
                            sx={{
                                color: '#FFFFFF',
                                backgroundColor: theme.palette.primary.main,
                                padding: '6px',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.3),
                                    color: alpha('#FFFFFF', 0.7),
                                },
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SendIcon fontSize="small" />
                            )}
                        </IconButton>
                    </ChatInputRow>
                </ChatInputContainer>
            </ChatContainer>

            {/* Comment options menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                elevation={2}
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        minWidth: 120,
                    }
                }}
            >
                <MenuItem
                    onClick={handleStartEditing}
                    sx={{
                        fontSize: '0.85rem',
                        py: 0.5
                    }}
                >
                    <EditIcon fontSize="small" sx={{ mr: 1, fontSize: '0.9rem' }} />
                    Editar
                </MenuItem>
                <MenuItem
                    onClick={handleDeleteComment}
                    sx={{
                        fontSize: '0.85rem',
                        py: 0.5,
                        color: theme.palette.error.main
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" sx={{ mr: 1, fontSize: '0.9rem' }} />
                    Excluir
                </MenuItem>
            </Menu>

            {/* Filters menu */}
            <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={() => setFilterMenuAnchor(null)}
                elevation={2}
            >
                <MenuItem
                    onClick={() => {
                        setFilter('all');
                        setFilterMenuAnchor(null);
                    }}
                    selected={filter === 'all'}
                    sx={{ fontSize: '0.85rem', py: 0.5 }}
                >
                    Todos
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setFilter('media');
                        setFilterMenuAnchor(null);
                    }}
                    selected={filter === 'media'}
                    sx={{ fontSize: '0.85rem', py: 0.5 }}
                >
                    Com mídia
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setFilter('text');
                        setFilterMenuAnchor(null);
                    }}
                    selected={filter === 'text'}
                    sx={{ fontSize: '0.85rem', py: 0.5 }}
                >
                    Apenas texto
                </MenuItem>
            </Menu>

            {/* Sort menu */}
            <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={() => setSortMenuAnchor(null)}
                elevation={2}
            >
                <MenuItem
                    onClick={() => {
                        if (sortOrder !== 'newest') {
                            setSortOrder('newest');
                            commentsInitialized.current = false;
                            setComments([]);
                            setPage(1);
                            setHasMore(true);

                            setTimeout(() => {
                                fetchCommentsRef.current(true);
                            }, 0);
                        }
                        setSortMenuAnchor(null);
                    }}
                    selected={sortOrder === 'newest'}
                    sx={{ fontSize: '0.85rem', py: 0.5 }}
                >
                    Mais recentes
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (sortOrder !== 'oldest') {
                            setSortOrder('oldest');
                            commentsInitialized.current = false;
                            setComments([]);
                            setPage(1);
                            setHasMore(true);

                            setTimeout(() => {
                                fetchCommentsRef.current(true);
                            }, 0);
                        }
                        setSortMenuAnchor(null);
                    }}
                    selected={sortOrder === 'oldest'}
                    sx={{ fontSize: '0.85rem', py: 0.5 }}
                >
                    Mais antigos
                </MenuItem>
            </Menu>

            {/* Delete confirmation dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => {
                    setOpenDeleteDialog(false);
                    setCurrentComment(null);
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: 320
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '1rem', py: 1.5 }}>Excluir mensagem</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography variant="body2">
                        Tem certeza que deseja excluir esta mensagem?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setOpenDeleteDialog(false);
                            setCurrentComment(null);
                        }}
                        sx={{
                            textTransform: 'none',
                            color: theme.palette.text.secondary
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Media view dialog */}
            <Dialog
                open={openMediaDialog}
                onClose={() => setOpenMediaDialog(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden'
                    }
                }}
            >
                <IconButton
                    onClick={() => setOpenMediaDialog(false)}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    component="img"
                    src={currentMediaUrl}
                    alt="Media"
                    sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                    }}
                />
            </Dialog>
        </>
    );
};

export default KanbanComments;
